import { AspectRatio } from '@/components/AspectRatioDropdown/AspectRatioDropdown';
import { UploadingMediaEntity } from '@/pages/AdvancedEditor/components/panels/media/types';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import { ProjectPagesImages } from '@/pages/AdvancedEditor/hooks';
import { DEFAULT_VIDEO_NAME } from '@/state/presenters';
import { CropMode } from '@/utils/types';
import { StoreType } from 'polotno/model/store';
import { ProjectAutoSaveStatus } from '../../types';

export enum PolotnoStoreKeys {
    creativeVideoName = 'creativeVideoName',
    backgroundLoading = 'backgroundLoading',
    backgroundReady = 'backgroundReady',
    cropSettings = 'cropSettings',
    aspectRatio = 'aspectRatio',
    projectPagesImages = 'projectPagesImages',
    projectPagesHashes = 'projectPagesHashes',
    subtitlesActive = 'subtitlesActive',
    uploadingMedia = 'uploadingMedia',
    projectAutoSaveStatus = 'projectAutoSaveStatus',
}

interface PolotnoStoreState {
    [PolotnoStoreKeys.creativeVideoName]: string;
    [PolotnoStoreKeys.backgroundLoading]: Set<string>;
    [PolotnoStoreKeys.backgroundReady]: string | undefined;
    [PolotnoStoreKeys.cropSettings]: CropMode;
    [PolotnoStoreKeys.aspectRatio]: AspectRatio;
    [PolotnoStoreKeys.projectPagesImages]: ProjectPagesImages;
    [PolotnoStoreKeys.projectPagesHashes]: Record<string, number | string>;
    [PolotnoStoreKeys.subtitlesActive]: boolean;
    [PolotnoStoreKeys.uploadingMedia]: Map<string, UploadingMediaEntity>;
    [PolotnoStoreKeys.projectAutoSaveStatus]: ProjectAutoSaveStatus;
}

const polotnoStoreDefaults: Record<PolotnoStoreKeys, { defaultValue: any; persistence: boolean }> = {
    [PolotnoStoreKeys.creativeVideoName]: {
        defaultValue: DEFAULT_VIDEO_NAME,
        persistence: true,
    },
    [PolotnoStoreKeys.backgroundLoading]: {
        defaultValue: new Set<string>(),
        persistence: false,
    },
    [PolotnoStoreKeys.backgroundReady]: {
        defaultValue: undefined,
        persistence: false,
    },
    [PolotnoStoreKeys.cropSettings]: {
        defaultValue: CropMode.Wide,
        persistence: true,
    },
    [PolotnoStoreKeys.aspectRatio]: {
        defaultValue: AspectRatio.Landscape,
        persistence: true,
    },
    [PolotnoStoreKeys.projectPagesImages]: {
        defaultValue: {},
        persistence: false,
    },
    [PolotnoStoreKeys.projectPagesHashes]: {
        defaultValue: {},
        persistence: false,
    },
    [PolotnoStoreKeys.subtitlesActive]: {
        defaultValue: false,
        persistence: true,
    },
    [PolotnoStoreKeys.uploadingMedia]: {
        defaultValue: new Map(),
        persistence: false,
    },
    [PolotnoStoreKeys.projectAutoSaveStatus]: {
        defaultValue: ProjectAutoSaveStatus.Idle,
        persistence: false,
    },
};

type PolotnoStoreStateValue<K extends PolotnoStoreKeys> = PolotnoStoreState[K];

export const usePolotnoState = <K extends PolotnoStoreKeys>(
    key: K
): readonly [PolotnoStoreStateValue<K>, (value: PolotnoStoreStateValue<K>) => void] => {
    const { store } = useAdvancedEditorContext();
    const { persistence, defaultValue } = polotnoStoreDefaults[key];
    const storage = persistence ? 'custom' : ('temporary' as unknown as 'custom');

    const setter = (value: PolotnoStoreStateValue<K>) => {
        store.set({
            [storage]: {
                ...(store?.[storage] || {}),
                [key]: value,
            },
        });
    };

    return [(store?.[storage]?.[key] as PolotnoStoreStateValue<K> | undefined) ?? defaultValue, setter] as const;
};

export const useSetPolotnoState = <K extends PolotnoStoreKeys>(key: K): ((value: PolotnoStoreStateValue<K>) => void) => {
    const [_, setter] = usePolotnoState(key);

    return setter;
};

export const getStoreProperty = <K extends PolotnoStoreKeys>(store: StoreType, key: K): PolotnoStoreStateValue<K> => {
    const { persistence } = polotnoStoreDefaults[key];
    const storage = persistence ? 'custom' : ('temporary' as unknown as 'custom');

    return store?.[storage]?.[key] ?? polotnoStoreDefaults[key].defaultValue;
};

export const setStoreProperty =
    <K extends PolotnoStoreKeys>(store: StoreType, key: K) =>
    (value: PolotnoStoreStateValue<K>): void => {
        const { persistence } = polotnoStoreDefaults[key];
        const storage = persistence ? 'custom' : ('temporary' as unknown as 'custom');

        return store.set({
            [storage]: {
                ...(store?.[storage] || {}),
                [key]: value,
            },
        });
    };
